import { useTranslation } from 'react-i18next';
import { MainCustomButton } from '../../components/Buttons';
import { OnboardingHeader } from '../../components/OnboardingHeader';
import { CustomInput } from '../../components/inputs';
import * as Yup from 'yup';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ShareholderService } from '../../services/ShareholderService';
import { errorToast, successToast } from '../../utils/toasts';
import { errorMessageAdapter } from '../../adapters';
import { AxiosError, AxiosInstance } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import i18n from '../../plugins/i18n/i18n';
import { useApi } from '../../services/hooks/apiHook';
import { useAppSelector } from '../../hooks/hooksRedux';
import { RootState } from '../../store';
import { defaultColors } from '../../styles/style-guide';
import { useState } from 'react';

interface PasswordResetForm {
  password: string;
  password_confirmation: string;
}

const ShareholderChangePasswordPage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { idSlug } = useParams<{ idSlug: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);

  const primaryColor = colors.primary || defaultColors.primary;

  const api: AxiosInstance = useApi();

  const validationSchema = Yup.object().shape({
    /* eslint-disable no-useless-escape */
    password: Yup.string()
      .required(t('validations.valRequired') || '')
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/, t('validations.valPwdRule') || ''),
    password_confirmation: Yup.string()
      .required(t('validations.valRequired') || '')
      .oneOf([Yup.ref('password')], t('validations.valPwdMustMatch') || '')
  });

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<PasswordResetForm>({
    resolver: yupResolver(validationSchema)
  });

  const handleSubmitForm: SubmitHandler<PasswordResetForm> = async (data: PasswordResetForm) => {
    if (!idSlug) {
      errorToast(t('errors.invalidUserIdentification'));
      return;
    }

    setIsSubmitting(true);

    try {
      await ShareholderService.createPassword(
        api,
        {
          user_slug: idSlug,
          password: data.password,
          password_confirmation: data.password_confirmation
        },
        {
          headers: { 'Content-Language': i18n.language.toLowerCase() }
        }
      );

      successToast(t('others.othPasswordChangedSuccess'));
      
      // Redirect to login page after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (e: any) {
      errorToast(errorMessageAdapter(e as AxiosError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen justify-between">
      <div
        className="scrollbar-hidden h-screen w-full overflow-y-scroll bg-contain bg-local bg-bottom bg-no-repeat px-6 py-6 md:px-12"
        style={{
          backgroundImage: 'url(/img/bg1.png)'
        }}
      >
        <OnboardingHeader />
        <form
          id="MainForm"
          className="m-auto mt-6 flex w-full flex-col items-start justify-center gap-4 md:mt-10 md:w-5/12 lg:w-4/12"
          onSubmit={handleSubmit(handleSubmitForm)}
        >
          <h1 className="w-full text-left text-2xl font-bold md:text-3xl" style={{ color: primaryColor }}>
            {t('others.othChangePassword')} <br />
            <span className="text-black">Offshore2go</span>
          </h1>

          <p className="mb-2 w-full text-sm text-gray-600 md:mb-4 md:text-base">
            {t('others.othChangePasswordDescription')}
          </p>

          <div className="flex w-full flex-col gap-6 md:gap-12">
            <CustomInput
              id="password"
              label={t('labels.lblPassword')}
              placeholder={t('fields.fldConfirmPwdPlaceholder') || ''}
              type="password"
              tooltip={t('validations.valPwdAdvice') as string}
              error={errors.password?.message}
              register={register}
              autoComplete="off"
            />
            <CustomInput
              id="password_confirmation"
              label={t('labels.lblConfirmPassword')}
              placeholder={t('fields.fldConfirmPwdPlaceholder') || ''}
              type="password"
              error={errors.password_confirmation?.message}
              register={register}
              autoComplete="off"
            />
          </div>
          <MainCustomButton
            text={isSubmitting ? t('others.othProcessing') : t('buttons.btnResetPwd')}
            form="MainForm"
            disabled={isSubmitting}
          />
        </form>
      </div>
    </div>
  );
};

export default ShareholderChangePasswordPage;

